import { DialogTrigger, Flex, Image, Link, LogicButton, View } from '@adobe/react-spectrum';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

// Shared libs
import { WithSkeleton } from '@exchange-frontends/components';
import { useMediaQuery, useRootHref } from '@exchange-frontends/custom-hooks';

import { SAP_CODE_TO_ICON_ID } from '../../../../utils/Constants';
import DebugMode, { dateFormatter } from '../../../DebugMode';
import InstallInstructionsDialog from '../InstallInstructionsDialog';
import {
  Badge,
  CardBody,
  DescriptionElement,
  ImageContainer,
  PublisherElement,
  StyledCard,
  SupportedProductIcon,
  TitleElement,
} from './card.styles';

/**
 * ManageCard component expands on the react-spectrum Card with additional styling
 * @component
 * @category Manage
 * @property {app} object - object representing the app
 * @property {boolean} [roundedBottom=true] - allows you to control if the bottom corners should be rounded or not
 * @property {boolean} [roundedTop=true] - allows you to control if the top corners should be rounded or not
 * @property {boolean} [hideButton] - if set to `true` it will not render the button
 * @property {function} [onButtonPress] - function to be called when pressing the button (not required is button is hidden)
 */
const ManageCard = (props) => {
  const {
    loading,
    app,
    roundedBottom = true,
    roundedTop = true,
    hideButton,
    customButton: CustomButton,
    titleLink,
    ...rest
  } = props;
  // Custom Hooks
  const isMobileSized = useMediaQuery('(max-width: 800px)');

  const rootTitleLink = useRootHref(titleLink ?? '');

  return (
    <StyledCard $roundedBottom={roundedBottom} $roundedTop={roundedTop} allowsSelection={false} {...rest}>
      <DebugMode>
        <Flex width="100%" gap="size-50" marginStart="size-50" marginTop="size-50">
          <LogicButton>appNumber: {app?.appNumber}</LogicButton>
          <LogicButton>acquiredDate: {dateFormatter.format(new Date(app?.acquiredDate ?? 0))}</LogicButton>
        </Flex>
      </DebugMode>
      <CardBody $isMobileSized={isMobileSized}>
        <Flex
          alignItems="center"
          flex="1"
          marginBottom={isMobileSized ? 'size-125' : '0'}
          maxWidth={isMobileSized ? '100%' : '40%'}
        >
          <ImageContainer>
            <WithSkeleton shouldRender={!loading} width="60px" height="60px">
              <Image alt="App icon" width="60px" height="60px" src={app?.iconUrl} />
            </WithSkeleton>
          </ImageContainer>
          <View marginEnd="auto">
            <TitleElement>
              <WithSkeleton shouldRender={!loading} width={200}>
                {titleLink ? (
                  <RouterLink style={{ color: 'inherit', textDecoration: 'none' }} to={rootTitleLink}>
                    {app?.title}
                  </RouterLink>
                ) : (
                  app?.title
                )}
              </WithSkeleton>
            </TitleElement>
            <PublisherElement>
              <WithSkeleton shouldRender={!loading} width={100}>
                {app?.publisherName && app.publisherName}
              </WithSkeleton>
            </PublisherElement>
            {app?.supportedProducts?.length && (
              <Flex marginTop="size-75" gap="size-50">
                {app.supportedProducts.map(({ app }) => (
                  <SupportedProductIcon key={app} viewBox="0 0 240 234">
                    <use xlinkHref={`#${SAP_CODE_TO_ICON_ID[app]}`}></use>
                  </SupportedProductIcon>
                ))}
              </Flex>
            )}
          </View>
        </Flex>
        <Flex
          direction="column"
          alignItems="start"
          flex="1"
          marginBottom={isMobileSized ? 'size-125' : '0'}
          maxWidth={isMobileSized ? '100%' : '40%'}
        >
          <DescriptionElement>
            <WithSkeleton shouldRender={!loading} width={200}>
              {app?.description}
            </WithSkeleton>
          </DescriptionElement>
          <WithSkeleton shouldRender={!loading} width={150}>
            {app?.jaeger && <Badge>Adobe Developer App Builder</Badge>}
          </WithSkeleton>
          <WithSkeleton shouldRender={!loading} width={110}>
            {app?.installationInstructions && (
              <DialogTrigger>
                <Link>Install instructions</Link>
                {(close) => (
                  <InstallInstructionsDialog
                    close={close}
                    title={app?.title}
                    icon={app?.iconUrl}
                    description={app?.description}
                    instructions={app?.installationInstructions}
                  />
                )}
              </DialogTrigger>
            )}
          </WithSkeleton>
        </Flex>
        {!hideButton && CustomButton && <CustomButton />}
      </CardBody>
    </StyledCard>
  );
};

export default ManageCard;
